import React, { useState } from 'react'
import { validateEmail, validateRequired } from '@lib/validation'
import Fetch from '@lib/fetch'

const MailingListSignupForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    whatsonEmails: false,
    offersEmails: false
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errors, setErrors] = useState({})

  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setFormData({
      ...formData,
      [field]: value
    })
    if (errors[field]) {
      setErrors({ ...errors, [field]: null })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!validateRequired(formData.firstName)) {
      newErrors.firstName = 'First name is required'
    }

    if (!validateRequired(formData.lastName)) {
      newErrors.lastName = 'Last name is required'
    }

    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email address is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.whatsonEmails && !formData.offersEmails) {
      newErrors.preferences = 'Please select at least one mailing preference'
    }

    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)

    try {
      const response = await Fetch('/mailing-list/signup', {
        method: 'POST',
        body: JSON.stringify({
          mailing_list_signup: {
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            email: formData.email.trim(),
            whatson_emails: formData.whatsonEmails,
            offers_emails: formData.offersEmails
          }
        })
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(true)
      } else {
        setErrors({
          submit: data.errors?.join(', ') || 'An error occurred. Please try again.'
        })
      }
    } catch (error) {
      console.error('Signup failed:', error)
      setErrors({
        submit: 'An error occurred. Please try again.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-green-400 dark:text-green-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-green-800 dark:text-green-200">
              Please check your email
            </h3>
            <div className="mt-2 text-green-700 dark:text-green-300">
              <p>
                We've sent a confirmation email to <strong>{formData.email}</strong>.
                Please click the link in the email to confirm your subscription.
              </p>
              <p className="mt-2 text-sm">
                The link will expire in 24 hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit && (
        <div className="bg-red-50 dark:bg-red-900 dark:bg-opacity-20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="firstName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            First Name
            <span className="text-red-500 dark:text-red-400 ml-1">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            value={formData.firstName}
            onChange={handleInputChange('firstName')}
            className={`w-full px-3 py-2 border ${
              errors.firstName
                ? 'border-red-500 dark:border-red-400'
                : 'border-gray-300 dark:border-gray-600'
            } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
            disabled={isSubmitting}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.firstName}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="lastName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Last Name
            <span className="text-red-500 dark:text-red-400 ml-1">*</span>
          </label>
          <input
            type="text"
            id="lastName"
            value={formData.lastName}
            onChange={handleInputChange('lastName')}
            className={`w-full px-3 py-2 border ${
              errors.lastName
                ? 'border-red-500 dark:border-red-400'
                : 'border-gray-300 dark:border-gray-600'
            } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
            disabled={isSubmitting}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lastName}</p>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Email Address
          <span className="text-red-500 dark:text-red-400 ml-1">*</span>
        </label>
        <input
          type="email"
          id="email"
          value={formData.email}
          onChange={handleInputChange('email')}
          className={`w-full px-3 py-2 border ${
            errors.email
              ? 'border-red-500 dark:border-red-400'
              : 'border-gray-300 dark:border-gray-600'
          } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
          placeholder="you@example.com"
          disabled={isSubmitting}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
        )}
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Email Preferences
          <span className="text-red-500 dark:text-red-400 ml-1">*</span>
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose what you'd like to hear about
        </p>

        {errors.preferences && (
          <p className="text-sm text-red-600 dark:text-red-400">{errors.preferences}</p>
        )}

        <div className="space-y-3">
          <label className="flex items-start p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={formData.whatsonEmails}
              onChange={handleInputChange('whatsonEmails')}
              className="mt-1 h-4 w-4 text-blue-600 dark:text-blue-500 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
              disabled={isSubmitting}
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                I am happy to receive weekly What's On emails
              </span>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Get our weekly round-up of upcoming events and shows
              </p>
            </div>
          </label>

          <label className="flex items-start p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <input
              type="checkbox"
              checked={formData.offersEmails}
              onChange={handleInputChange('offersEmails')}
              className="mt-1 h-4 w-4 text-blue-600 dark:text-blue-500 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
              disabled={isSubmitting}
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                I am happy to receive emails about future events and offers
              </span>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Be the first to hear about new shows and exclusive offers
              </p>
            </div>
          </label>
        </div>
      </div>

      <div className="text-sm text-gray-600 dark:text-gray-400">
        <p>
          By signing up, you agree to our{' '}
          <a href="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">
            Privacy Policy
          </a>
          . You can unsubscribe at any time.
        </p>
      </div>

      <button
        type="submit"
        className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Signing Up...
          </span>
        ) : (
          'Sign Up'
        )}
      </button>
    </form>
  )
}

export default MailingListSignupForm
